import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Packages = () => {
  const { language, t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        setPackages(response.data);
      } catch (error) {
        console.error('Error fetching packages:', error);
        // Fallback data
        setPackages([
          {
            id: '1',
            name_ar: 'الباقة الأساسية',
            name_en: 'Basic Plan',
            description_ar: 'مثالية للمشاريع الصغيرة والبدايات',
            description_en: 'Perfect for small projects and startups',
            price: 99,
            currency: 'SAR',
            messages_limit: 1000,
            features_ar: ['1000 رسالة شهرياً', 'ربط حساب واحد', 'دعم فني عبر البريد', 'تقارير أساسية'],
            features_en: ['1000 messages/month', '1 account connection', 'Email support', 'Basic reports'],
            is_popular: false,
          },
          {
            id: '2',
            name_ar: 'الباقة الاحترافية',
            name_en: 'Professional Plan',
            description_ar: 'للشركات المتوسطة التي تحتاج المزيد',
            description_en: 'For medium businesses that need more',
            price: 299,
            currency: 'SAR',
            messages_limit: 5000,
            features_ar: ['5000 رسالة شهرياً', 'ربط 3 حسابات', 'دعم فني أولوية', 'تقارير متقدمة', 'API كامل'],
            features_en: ['5000 messages/month', '3 account connections', 'Priority support', 'Advanced reports', 'Full API'],
            is_popular: true,
          },
          {
            id: '3',
            name_ar: 'باقة الشركات',
            name_en: 'Enterprise Plan',
            description_ar: 'حلول مخصصة للشركات الكبيرة',
            description_en: 'Custom solutions for large enterprises',
            price: 799,
            currency: 'SAR',
            messages_limit: 20000,
            features_ar: ['20000 رسالة شهرياً', 'ربط غير محدود', 'مدير حساب مخصص', 'تقارير مخصصة', 'API متقدم', 'SLA مضمون'],
            features_en: ['20000 messages/month', 'Unlimited connections', 'Dedicated account manager', 'Custom reports', 'Advanced API', 'Guaranteed SLA'],
            is_popular: false,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="packages" className="section-spacing" data-testid="packages-section">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            {t('packages_badge')}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="packages-title">
            {t('packages_title')}{' '}
            <span className="gradient-text">{t('packages_title_highlight')}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('packages_subtitle')}
          </p>
        </motion.div>

        {/* Packages Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card-pricing animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/2 mb-4" />
                <div className="h-12 bg-slate-200 rounded w-1/3 mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-4 bg-slate-200 rounded w-full" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                variants={itemVariants}
                className={`card-pricing ${
                  pkg.is_popular
                    ? 'border-2 border-emerald-500 shadow-xl shadow-emerald-500/10 scale-105'
                    : ''
                }`}
                data-testid={`package-card-${index}`}
              >
                {pkg.is_popular && (
                  <div className="absolute -top-4 start-1/2 -translate-x-1/2 px-4 py-1 rounded-full popular-badge text-white text-sm font-medium">
                    {t('packages_popular')}
                  </div>
                )}

                {/* Package Header */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {language === 'ar' ? pkg.name_ar : pkg.name_en}
                  </h3>
                  <p className="text-slate-500 text-sm">
                    {language === 'ar' ? pkg.description_ar : pkg.description_en}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-slate-900">{pkg.price}</span>
                    <span className="text-slate-500">{t('packages_currency')}</span>
                    <span className="text-slate-400 text-sm">{t('packages_month')}</span>
                  </div>
                  <div className="text-emerald-600 font-medium mt-1">
                    {pkg.messages_limit.toLocaleString()} {t('packages_messages')}
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {(language === 'ar' ? pkg.features_ar : pkg.features_en).map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  className={`w-full ${
                    pkg.is_popular ? 'btn-primary' : 'btn-secondary'
                  }`}
                  data-testid={`package-cta-${index}`}
                >
                  {t('packages_cta')}
                </Button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Packages;
